import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, Check, X, Calendar as CalendarIcon, Clock, MapPin, Phone, AlertCircle,
  ChevronLeft, ChevronRight, ArrowLeft,
} from "lucide-react";
import { DayPicker } from "react-day-picker";
import { pt } from "date-fns/locale";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import SeoHead from "../components/SeoHead";
import {
  getMarcacaoPorToken, cancelarMarcacao, reagendarMarcacao,
  getDisponibilidade, icsUrl,
} from "../lib/api";
import { formatarDataHoraPT, combinarDataHora, isoLocal } from "../lib/datetime";
import {
  Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from "../components/ui/dialog";

const ESTADOS_LABEL = {
  pendente: { l: "Pendente de confirmação", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  confirmada: { l: "Confirmada", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelada: { l: "Cancelada", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  concluida: { l: "Concluída", cls: "bg-[#1E5F6E]/10 text-[#1E5F6E] border-[#1E5F6E]/20" },
  no_show: { l: "Não compareceu", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

export default function MarcacaoTokenPage() {
  const { token } = useParams();
  const [carregando, setCarregando] = useState(true);
  const [marcacao, setMarcacao] = useState(null);
  const [erro, setErro] = useState(null);

  const [cancelando, setCancelando] = useState(false);
  const [reagOpen, setReagOpen] = useState(false);
  const [novaData, setNovaData] = useState(null);
  const [novaHora, setNovaHora] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsRazao, setSlotsRazao] = useState(null);
  const [reagSubmitting, setReagSubmitting] = useState(false);
  const [reagErro, setReagErro] = useState(null);

  const carregar = () => {
    setCarregando(true);
    getMarcacaoPorToken(token)
      .then(setMarcacao)
      .catch((e) => setErro(e?.response?.data?.detail || "Marcação não encontrada."))
      .finally(() => setCarregando(false));
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!reagOpen || !novaData || !marcacao) {
      setSlots([]);
      return;
    }
    let active = true;
    setSlotsLoading(true);
    setSlotsRazao(null);
    getDisponibilidade({
      clinica: marcacao.clinica.slug,
      servico: marcacao.servico.slug,
      data: isoLocal(novaData),
    })
      .then((r) => {
        if (!active) return;
        setSlots(r.slots || []);
        setSlotsRazao(r.razao || null);
      })
      .catch(() => active && setSlotsRazao("Erro a obter horários."))
      .finally(() => active && setSlotsLoading(false));
    return () => { active = false; };
  }, [novaData, reagOpen, marcacao]);

  const onCancelar = async () => {
    setCancelando(true);
    try {
      await cancelarMarcacao(token);
      carregar();
    } catch (e) {
      setErro(e?.response?.data?.detail || "Não foi possível cancelar.");
    } finally {
      setCancelando(false);
    }
  };

  const onConfirmarReagendar = async () => {
    if (!novaData || !novaHora) return;
    setReagSubmitting(true);
    setReagErro(null);
    try {
      const dt = combinarDataHora(novaData, novaHora);
      const updated = await reagendarMarcacao(token, dt.toISOString());
      setMarcacao(updated);
      setReagOpen(false);
      setNovaData(null);
      setNovaHora(null);
    } catch (e) {
      setReagErro(e?.response?.data?.detail || "Não foi possível reagendar.");
    } finally {
      setReagSubmitting(false);
    }
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const maxDate = new Date(); maxDate.setDate(maxDate.getDate() + 60);
  const isCancelable = marcacao && ["pendente", "confirmada"].includes(marcacao.estado)
    && new Date(marcacao.data_hora) > new Date(Date.now() + 4 * 3600 * 1000);
  const isModifiable = marcacao && ["pendente", "confirmada"].includes(marcacao.estado)
    && new Date(marcacao.data_hora) > new Date();

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1A1A1A]" data-testid="marcacao-token-page">
      <SeoHead title="Gerir marcação | Vet Clinic" description="Veja, reagende ou cancele a sua marcação." />
      <Header />
      <main className="container-app py-12 md:py-16 max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-sm font-bold text-[#1E5F6E] mb-6 hover:underline underline-offset-4">
          <ArrowLeft className="h-4 w-4" /> Voltar à página inicial
        </Link>

        {carregando && (
          <div className="flex items-center gap-3 text-[#5A5A5A]" data-testid="loading-state">
            <Loader2 className="h-5 w-5 animate-spin" /> A carregar marcação...
          </div>
        )}

        {!carregando && erro && (
          <div className="rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 p-5" data-testid="not-found">
            <div className="flex items-center gap-2 font-bold"><AlertCircle className="h-5 w-5" /> {erro}</div>
            <Link to="/marcar-consulta" className="mt-3 inline-block text-sm font-bold underline">
              Marcar nova consulta →
            </Link>
          </div>
        )}

        {!carregando && marcacao && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-[#88A096]">Marcação</div>
                <div className="font-display text-3xl md:text-4xl font-extrabold text-[#1A1A1A] tracking-tight">
                  {marcacao.numero_marcacao}
                </div>
              </div>
              <span
                data-testid="estado-badge"
                className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-bold border ${ESTADOS_LABEL[marcacao.estado]?.cls}`}
              >
                {ESTADOS_LABEL[marcacao.estado]?.l || marcacao.estado}
              </span>
            </div>

            <div className="rounded-3xl bg-white border border-[#1E5F6E]/10 p-6 md:p-8 shadow-soft space-y-6">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-[#88A096] mb-1">Quando</div>
                <div className="font-display text-xl md:text-2xl font-extrabold text-[#1A1A1A]">
                  {formatarDataHoraPT(marcacao.data_hora)}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-[#88A096] mb-1">Clínica</div>
                  <div className="font-bold text-[#1A1A1A] flex items-center gap-2"><MapPin className="h-4 w-4 text-[#88A096]" />{marcacao.clinica.nome}</div>
                  <div className="text-sm text-[#5A5A5A] mt-1">{marcacao.clinica.morada}, {marcacao.clinica.codigo_postal} {marcacao.clinica.cidade}</div>
                  <a href={`tel:${marcacao.clinica.telefone_e164}`} className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-bold text-[#1E5F6E]">
                    <Phone className="h-3.5 w-3.5" />{marcacao.clinica.telefone}
                  </a>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-[#88A096] mb-1">Serviço</div>
                  <div className="font-bold text-[#1A1A1A] flex items-center gap-2"><Clock className="h-4 w-4 text-[#88A096]" />{marcacao.servico.nome}</div>
                  <div className="text-sm text-[#5A5A5A] mt-1">Duração: {marcacao.servico.duracao_minutos} min</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-[#88A096] mb-1">Tutor</div>
                  <div className="font-bold text-[#1A1A1A]">{marcacao.tutor.nome}</div>
                  <div className="text-sm text-[#5A5A5A]">{marcacao.tutor.email}</div>
                  <div className="text-sm text-[#5A5A5A]">{marcacao.tutor.telefone}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-[#88A096] mb-1">Animal</div>
                  <div className="font-bold text-[#1A1A1A]">{marcacao.animal.nome}</div>
                  {marcacao.animal.especie && <div className="text-sm text-[#5A5A5A] capitalize">{marcacao.animal.especie}</div>}
                </div>
              </div>
              {marcacao.motivo && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-[#88A096] mb-1">Motivo</div>
                  <p className="text-sm text-[#1A1A1A] whitespace-pre-line">{marcacao.motivo}</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={icsUrl(token)}
                data-testid="ics-download"
                className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-[#1E5F6E]/15 px-5 py-2.5 text-sm font-bold text-[#1E5F6E] hover:border-[#1E5F6E]/40 transition-colors"
              >
                <CalendarIcon className="h-4 w-4" />
                Adicionar ao calendário
              </a>

              {isModifiable && (
                <Dialog open={reagOpen} onOpenChange={setReagOpen}>
                  <DialogTrigger asChild>
                    <button
                      data-testid="btn-reagendar"
                      className="inline-flex items-center gap-2 rounded-full bg-[#1E5F6E] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#174E5B] transition-colors"
                    >
                      <CalendarIcon className="h-4 w-4" />
                      Reagendar
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl">
                    <DialogTitle>Reagendar marcação</DialogTitle>
                    <DialogDescription>
                      Escolha uma nova data e horário disponíveis na clínica.
                    </DialogDescription>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
                      <div className="rounded-2xl bg-[#FAF7F2] border border-[#1E5F6E]/10 p-2" data-testid="reag-calendar">
                        <DayPicker
                          mode="single"
                          selected={novaData}
                          onSelect={(d) => { setNovaHora(null); setNovaData(d || null); }}
                          locale={pt}
                          fromDate={today}
                          toDate={maxDate}
                          disabled={(d) => d < today || d > maxDate || d.getDay() === 0}
                          classNames={{
                            months: "flex flex-col",
                            month: "space-y-4 w-full",
                            caption: "flex items-center justify-center pt-1 relative",
                            caption_label: "text-sm font-display font-bold text-[#1A1A1A] capitalize",
                            nav: "absolute inset-x-0 flex justify-between px-2",
                            nav_button: "h-7 w-7 inline-flex items-center justify-center rounded-lg bg-white border border-[#1E5F6E]/15 text-[#1E5F6E]",
                            table: "w-full border-collapse",
                            head_row: "flex w-full",
                            head_cell: "flex-1 text-[10px] text-[#5A5A5A] font-bold uppercase py-2",
                            row: "flex w-full",
                            cell: "flex-1 p-0.5",
                            day: "h-9 w-full inline-flex items-center justify-center rounded-lg text-xs font-medium text-[#1A1A1A] hover:bg-[#1E5F6E]/10",
                            day_selected: "!bg-[#1E5F6E] !text-white",
                            day_today: "ring-1 ring-[#E8A87C]",
                            day_disabled: "!text-rose-300 line-through cursor-not-allowed",
                            day_outside: "text-[#5A5A5A]/30",
                          }}
                          components={{
                            IconLeft: () => <ChevronLeft className="h-4 w-4" />,
                            IconRight: () => <ChevronRight className="h-4 w-4" />,
                          }}
                        />
                      </div>
                      <div>
                        <h4 className="font-display font-bold text-[#1A1A1A] mb-3">Horários</h4>
                        {!novaData && <p className="text-sm text-[#5A5A5A]">Escolha primeiro uma data.</p>}
                        {novaData && slotsLoading && <p className="text-sm text-[#5A5A5A] flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />A procurar...</p>}
                        {novaData && !slotsLoading && slotsRazao && <p className="text-sm text-[#7a3e1a]">{slotsRazao}</p>}
                        {novaData && !slotsLoading && !slotsRazao && slots.length === 0 && <p className="text-sm text-[#7a3e1a]">Sem horários disponíveis.</p>}
                        {novaData && !slotsLoading && slots.length > 0 && (
                          <div className="grid grid-cols-3 gap-2 max-h-[280px] overflow-y-auto pr-1">
                            {slots.map((s) => (
                              <button
                                key={s.hora}
                                disabled={!s.disponivel}
                                onClick={() => setNovaHora(s.hora)}
                                data-testid={`reag-slot-${s.hora}`}
                                className={`rounded-lg py-2 text-sm font-semibold ${
                                  novaHora === s.hora
                                    ? "bg-[#1E5F6E] text-white"
                                    : s.disponivel
                                    ? "bg-[#FAF7F2] text-[#1A1A1A] hover:bg-[#1E5F6E]/10"
                                    : "bg-[#FAF7F2] text-[#5A5A5A]/40 line-through cursor-not-allowed"
                                }`}
                              >
                                {s.hora}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {reagErro && <p className="mt-3 text-sm text-rose-600" data-testid="reag-erro">{reagErro}</p>}
                    <DialogFooter className="mt-5">
                      <button
                        onClick={() => setReagOpen(false)}
                        className="rounded-full bg-white border-2 border-[#1E5F6E]/15 px-5 py-2.5 text-sm font-bold text-[#1E5F6E]"
                      >Cancelar</button>
                      <button
                        onClick={onConfirmarReagendar}
                        disabled={!novaData || !novaHora || reagSubmitting}
                        data-testid="btn-reag-confirmar"
                        className="rounded-full bg-[#1E5F6E] px-5 py-2.5 text-sm font-bold text-white disabled:opacity-40 hover:bg-[#174E5B]"
                      >
                        {reagSubmitting ? "A reagendar..." : "Confirmar"}
                      </button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}

              {isCancelable && (
                <Dialog>
                  <DialogTrigger asChild>
                    <button
                      data-testid="btn-cancelar"
                      className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-rose-200 px-5 py-2.5 text-sm font-bold text-rose-700 hover:bg-rose-50 transition-colors"
                    >
                      <X className="h-4 w-4" />
                      Cancelar marcação
                    </button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogTitle>Cancelar marcação?</DialogTitle>
                    <DialogDescription>
                      Esta ação não pode ser desfeita. A marcação ficará disponível para outros tutores.
                    </DialogDescription>
                    <DialogFooter className="mt-4">
                      <DialogClose asChild>
                        <button className="rounded-full bg-white border-2 border-[#1E5F6E]/15 px-5 py-2.5 text-sm font-bold text-[#1E5F6E]">
                          Voltar atrás
                        </button>
                      </DialogClose>
                      <button
                        onClick={onCancelar}
                        disabled={cancelando}
                        data-testid="btn-confirm-cancel"
                        className="rounded-full bg-rose-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-rose-700 disabled:opacity-40"
                      >
                        {cancelando ? "A cancelar..." : "Sim, cancelar"}
                      </button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {!isModifiable && (
              <p className="mt-6 text-sm text-[#5A5A5A]" data-testid="aviso-readonly">
                Esta marcação já não pode ser modificada online. Para alterações contacte a clínica.
              </p>
            )}
          </motion.div>
        )}
      </main>
      <Footer />
    </div>
  );
}
